const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['remove']
  }

  static get collapsedUpsellsKey () {
    return 'bao-cart-collapsed-upsells'
  }

  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.onSwitchToSubscription = this.onSwitchToSubscription.bind(this)
    this.onFrequencyChange = this.onFrequencyChange.bind(this)
    this.onToggleUpsell = this.onToggleUpsell.bind(this)
    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    if (this.els?.remove.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    if (this.els?.switchToSubscription?.exists) {
      this.listeners.add(
        this.els.switchToSubscription.element,
        'click',
        this.onSwitchToSubscription,
      )
    }

    if (this.els?.upsellToggle?.exists) {
      this.restoreUpsellState()

      this.listeners.add(
        this.els.upsellToggle.element,
        'click',
        this.onToggleUpsell,
      )
    }

    if (this.els?.frequency?.exists) {
      this.listeners.add(
        this.els.frequency.element,
        'change',
        this.onFrequencyChange,
      )
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeLineItem (e) {
    e.preventDefault()

    if (this.loading) return

    this.loading = true

    const state = await window.BAO.Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })

    this.loading = false

    if (state && 'sections' in state) this.renderSections(state.sections)
  }

  async onQuantityChange (e) {
    if (this.loading) return

    this.loading = true

    const state = await window.BAO.Cart.updateQuantity(
      this.dataset.key,
      e.detail.value,
      { sections: this.mappedSections },
    )

    this.loading = false

    if (!state) {
      this.handleCartErrorRender()

      return
    }

    if ('sections' in state) this.renderSections(state.sections)
  }

  async onSwitchToSubscription (e) {
    e.preventDefault()

    return this.updateSellingPlan(e.currentTarget.dataset.sellingPlan)
  }

  async onFrequencyChange (e) {
    return this.updateSellingPlan(e.currentTarget.value || null)
  }

  /**
   * @returns {string[]} Variant ids whose upsell the customer has collapsed
   */
  get collapsedUpsells () {
    try {
      return JSON.parse(
        window.sessionStorage.getItem(CartItem.collapsedUpsellsKey) || '[]',
      )
    } catch (err) {
      return []
    }
  }

  /**
   * @param {string[]} variantIds
   *
   * @returns {void}
   */
  set collapsedUpsells (variantIds) {
    try {
      window.sessionStorage.setItem(
        CartItem.collapsedUpsellsKey,
        JSON.stringify(variantIds),
      )
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {boolean} collapsed
   *
   * @returns {void}
   */
  setUpsellCollapsed (collapsed) {
    this.els.upsellToggle.element.setAttribute(
      'aria-expanded',
      (!collapsed).toString(),
    )
  }

  /**
   * @returns {void}
   */
  restoreUpsellState () {
    const { variantId } = this.els.upsell.element.dataset

    this.setUpsellCollapsed(this.collapsedUpsells.includes(variantId))
  }

  /**
   * @returns {void}
   */
  onToggleUpsell () {
    const { variantId } = this.els.upsell.element.dataset
    const collapsed =
      this.els.upsellToggle.element.getAttribute('aria-expanded') === 'true'
    const others = this.collapsedUpsells.filter((id) => id !== variantId)

    this.collapsedUpsells = collapsed ? [...others, variantId] : others
    this.setUpsellCollapsed(collapsed)
  }

  async updateSellingPlan (sellingPlan) {
    if (this.loading) return

    this.loading = true

    const state = await window.BAO.Cart.updateSellingPlan(
      this.dataset.key,
      sellingPlan,
      { sections: this.mappedSections, quantity: this.quantity },
    )

    this.loading = false

    if (!state) {
      this.handleCartErrorRender()

      return
    }

    if ('sections' in state) this.renderSections(state.sections)
  }

  /**
   * @returns {number|undefined}
   */
  get quantity () {
    return Number(this.dataset.quantity) || undefined
  }

  get mappedSections () {
    return this.sections.map((s) => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
